import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { TitleSlide, VisualLayer } from '@/components/scriptAudioAlign/courseArtifactRenderers';
import {
  fetchAuthenticatedAudioUrl,
  resolvePreviewLayout,
  visualTypeLabel,
} from '@/lib/scriptAudioAlignVisuals';

function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function TopicHeader({ title }) {
  return (
    <div className="shrink-0 border-b border-violet-500/20 bg-slate-950/95 px-6 py-3 backdrop-blur-sm">
      <h2 className="text-lg font-semibold text-white truncate">{title}</h2>
    </div>
  );
}

export function ScriptAudioAlignPreview({ result, onResultUpdate }) {
  const audioRef = useRef(null);
  const blobUrlRef = useRef('');
  const rafRef = useRef(null);
  const [playhead, setPlayhead] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [audioSrc, setAudioSrc] = useState('');
  const [audioError, setAudioError] = useState('');
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioReloadKey, setAudioReloadKey] = useState(0);
  const [fixingAudio, setFixingAudio] = useState(false);

  const previewState = useMemo(
    () => resolvePreviewLayout(
      result?.screenplay_events,
      result?.visual_elements,
      result?.visual_plan,
      playhead,
    ),
    [result?.screenplay_events, result?.visual_elements, result?.visual_plan, playhead],
  );

  const tickPlayhead = useCallback(() => {
    const el = audioRef.current;
    if (el && !el.paused) {
      setPlayhead(el.currentTime);
      rafRef.current = requestAnimationFrame(tickPlayhead);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!result?.audio_url) {
        setAudioSrc('');
        return;
      }
      setAudioError('');
      try {
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = '';
        }
        const cacheBustUrl = `${result.audio_url}${result.audio_url.includes('?') ? '&' : '?'}v=${audioReloadKey}`;
        const blobUrl = await fetchAuthenticatedAudioUrl(cacheBustUrl);
        if (cancelled) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        blobUrlRef.current = blobUrl;
        setAudioSrc(blobUrl);
      } catch (err) {
        if (!cancelled) setAudioError(err.message || 'Could not load audio');
      }
    };
    load();
    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = '';
      }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [result?.audio_url, audioReloadKey]);

  const expectedDuration = num(result?.duration_seconds);
  const needsAudioFix = expectedDuration > 5
    && audioDuration > 0
    && audioDuration < expectedDuration * 0.85;

  const handleFixAudio = async () => {
    if (!result?.job_id) return;
    setFixingAudio(true);
    try {
      const { data } = await api.post(`/script-audio-align/jobs/${result.job_id}/rebuild-master-audio`);
      onResultUpdate?.(data.result);
      setAudioReloadKey((k) => k + 1);
      toast.success('Master audio rebuilt — try playing again');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not rebuild audio');
    } finally {
      setFixingAudio(false);
    }
  };

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return undefined;
    const syncDuration = () => {
      const d = Number(el.duration);
      if (Number.isFinite(d) && d > 0) setAudioDuration(d);
    };
    const onPlay = () => {
      setPlaying(true);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(tickPlayhead);
    };
    const onPause = () => {
      setPlaying(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setPlayhead(el.currentTime);
    };
    // Keep visuals in sync when the user scrubs the audio scrubber (which does
    // not run the requestAnimationFrame loop, especially while paused) and as a
    // fallback if RAF is throttled (e.g. background tab).
    const onSeek = () => setPlayhead(el.currentTime);
    el.addEventListener('loadedmetadata', syncDuration);
    el.addEventListener('durationchange', syncDuration);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onPause);
    el.addEventListener('seeking', onSeek);
    el.addEventListener('seeked', onSeek);
    el.addEventListener('timeupdate', onSeek);
    syncDuration();
    return () => {
      el.removeEventListener('loadedmetadata', syncDuration);
      el.removeEventListener('durationchange', syncDuration);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onPause);
      el.removeEventListener('seeking', onSeek);
      el.removeEventListener('seeked', onSeek);
      el.removeEventListener('timeupdate', onSeek);
    };
  }, [audioSrc, tickPlayhead]);

  if (!result?.screenplay_events?.length || !result?.visual_elements?.length) {
    return null;
  }

  const {
    mode,
    triggersAt,
    triggerWord,
    layout,
    topicTitle,
    contentElements,
  } = previewState;
  const dimmed = mode === 'preview' || mode === 'upcoming';
  const totalDuration = Math.max(num(result.duration_seconds), num(audioDuration));
  const badgeElements = layout === 'lesson' ? contentElements : previewState.elements;

  return (
    <div className="space-y-4 rounded-2xl border p-5" style={{ borderColor: 'var(--glass-border)' }}>
      <div>
        <h2 className="text-lg font-semibold">Synced preview</h2>
        <p className="text-sm text-[var(--text-secondary)]">
          Visuals appear when the narrator speaks each trigger word.
        </p>
      </div>

      <div className="relative aspect-video rounded-xl overflow-hidden bg-slate-950 border border-slate-800">
        {layout === 'empty' ? (
          <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-sm">
            No visuals in plan
          </div>
        ) : layout === 'topic_intro' ? (
          <div className={`absolute inset-0 ${dimmed ? 'opacity-70' : ''}`}>
            <TitleSlide payload={{ heading: topicTitle }} intro />
          </div>
        ) : (
          <div className={`absolute inset-0 flex flex-col ${dimmed ? 'opacity-70' : ''}`}>
            <TopicHeader title={topicTitle} />
            <div className="flex-1 overflow-auto min-h-0">
              {contentElements.map((el) => (
                <VisualLayer key={el.cue_id} element={el} />
              ))}
            </div>
          </div>
        )}
        {dimmed && layout !== 'empty' ? (
          <div className="absolute top-2 left-2 right-2 text-center">
            <span className="text-[10px] px-2 py-1 rounded bg-slate-900/90 text-slate-300">
              {mode === 'preview'
                ? `Preview — press play to sync (first cue at ${num(triggersAt).toFixed(2)}s${triggerWord ? ` on “${triggerWord}”` : ''})`
                : `Next cue at ${num(triggersAt).toFixed(2)}s${triggerWord ? ` on “${triggerWord}”` : ''}`}
            </span>
          </div>
        ) : null}
        <div className="absolute bottom-2 left-2 right-2 flex flex-wrap gap-1">
          {badgeElements.map((el) => (
            <span key={el.cue_id} className="text-[10px] px-2 py-0.5 rounded bg-violet-900/80 text-violet-200">
              {visualTypeLabel(el.visual_type)}
            </span>
          ))}
        </div>
      </div>

      {audioError ? (
        <p className="text-xs text-red-400">{audioError}</p>
      ) : null}

      {audioSrc ? (
        <audio
          ref={audioRef}
          controls
          className="w-full h-9"
          preload="auto"
          src={audioSrc}
        />
      ) : (
        <p className="text-xs text-slate-500">Loading audio…</p>
      )}
      {needsAudioFix ? (
        <div className="rounded-lg border border-amber-500/30 bg-amber-950/20 px-3 py-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-amber-200">
            Browser sees {audioDuration.toFixed(1)}s but narration is {expectedDuration.toFixed(1)}s — audio may stop early.
          </p>
          <Button type="button" size="sm" variant="outline" disabled={fixingAudio} onClick={handleFixAudio}>
            {fixingAudio ? 'Fixing…' : 'Fix audio length'}
          </Button>
        </div>
      ) : null}
      <p className="text-[11px] text-slate-500 font-mono">
        Playhead: {num(playhead).toFixed(2)}s / {totalDuration.toFixed(2)}s
        {mode === 'active' ? ' · synced' : ''}
        {totalDuration > 0 && totalDuration < num(result.duration_seconds) * 0.85 ? (
          <span className="text-amber-400"> · audio length mismatch</span>
        ) : null}
      </p>
    </div>
  );
}
